// src/components/AdminRoute.jsx
//
// Route guard for /admin/*. Grants access iff the signed-in user has a
// platform-level JWT custom claim (founder / support / legacy admin).
//
// We deliberately do NOT honor `users/{uid}.role === 'admin'` anymore.
// That field lives in a document the user themselves can write to
// (firestore.rules allows self-writes on users/{uid}), which means the
// old check was trivially self-escalatable. Platform access now comes
// exclusively from JWT custom claims set server-side by
// scripts/platformClaims.mjs. See functions/core/roles.js and
// src/constants/roles.js for the canonical definition.
//
// Migration note: any existing "admin" users whose access came from the
// Firestore role field will lose access after this lands. Grant them a
// real claim with:
//     node scripts/platformClaims.mjs grant <email> founder
//
// Back-compat: we still accept a legacy `admin: true` or `role: 'admin'`
// custom claim (as some older tooling set those) in addition to the
// canonical `founder` / `support` flags checked by usePlatformClaims.

import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import Loader from '@/components/Loader';

export default function AdminRoute({ children }) {
  const { firebaseUser, loadingAuth } = useAuth();
  const { claims, isFounder, isSupport } = usePlatformClaims();
  const location = useLocation();

  // Still resolving auth state — show loader rather than flashing the
  // unauthorized page.
  if (loadingAuth) return <Loader />;

  // Signed out → login, preserving intended destination.
  if (!firebaseUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Legacy JWT claims, kept for back-compat with any token minted before
  // the founder/support split. New tooling only sets founder/support.
  const legacyAdminClaim =
    claims?.admin === true ||
    String(claims?.role || '').toLowerCase() === 'admin';

  const isAdmin = isFounder || isSupport || legacyAdminClaim;

  if (!isAdmin) {
    const reason =
      'Platform access required. This area is restricted to Scrimly staff.';
    return (
      <Navigate to="/unauthorized" state={{ from: location, reason }} replace />
    );
  }

  return children ? children : <Outlet />;
}
